/**
 * Read-only Anchor program builder.
 *
 * Wraps a `Connection` with a no-op wallet so that we can decode on-chain
 * account state via the IDL without ever signing transactions. Any attempt
 * to sign should throw — the explorer is read-only.
 */
import {
  AnchorProvider,
  Program,
  type Idl as AnchorIdl,
} from "@coral-xyz/anchor";
import {
  Connection,
  Keypair,
  PublicKey,
  type Transaction,
  type VersionedTransaction,
} from "@solana/web3.js";
import { IDL, PROGRAM_ID } from "@solblox/shared";

/**
 * Minimal wallet stub. Anchor's `AnchorProvider` only needs `publicKey`,
 * `signTransaction`, and `signAllTransactions` to satisfy the `Wallet`
 * interface. We hand back transactions unsigned for reads (Anchor never
 * actually signs when only fetching accounts) and throw on any real sign
 * attempt so misuse is loud.
 */
class ReadOnlyWallet {
  readonly publicKey: PublicKey;
  // Keep a throwaway keypair so payer-style code paths have *something*,
  // but we never expose the secret beyond this instance.
  private readonly keypair: Keypair;

  constructor() {
    this.keypair = Keypair.generate();
    this.publicKey = this.keypair.publicKey;
  }

  async signTransaction<T extends Transaction | VersionedTransaction>(
    _tx: T,
  ): Promise<T> {
    throw new Error("ReadOnlyWallet cannot sign transactions");
  }

  async signAllTransactions<T extends Transaction | VersionedTransaction>(
    _txs: T[],
  ): Promise<T[]> {
    throw new Error("ReadOnlyWallet cannot sign transactions");
  }
}

/** Build a read-only Anchor `Program` for LastTouch on the given connection. */
export function buildReadonlyProgram(connection: Connection): Program {
  const wallet = new ReadOnlyWallet();
  const provider = new AnchorProvider(connection, wallet, {
    commitment: "confirmed",
  });
  return new Program(IDL as unknown as AnchorIdl, provider);
}

/** Re-export so call-sites can build the PublicKey without re-importing. */
export function getProgramId(): PublicKey {
  return new PublicKey(PROGRAM_ID);
}
